import { useEffect, useMemo, useRef } from 'react';

import { logDebug } from './logger';
import { useUnreadCounts } from './unread_store';

const SIDEBAR_UNREAD_DEBUG_TAG = 'unreads-sidebar-v2';

// Each channel has its own unread count, so we need to subscribe to the unread counts for the channel ID
// This prevents a change in one channel from affecting/re-rendering everything.
export function useSingleChannelUnread(channelId) {
  // stable array ref - new array each render would thrash useUnreadCounts
  const channelIds = useMemo(() => (channelId ? [channelId] : []), [channelId]);

  const { counts } = useUnreadCounts(channelIds);
  const data = counts[channelId];
  const messages = data?.messages ?? 0;
  const mentions = data?.mentions ?? 0;
  const previousRef = useRef(null);

  useEffect(() => {
    const previous = previousRef.current;
    const next = { messages, mentions };

    const changed = !previous || previous.messages !== next.messages || previous.mentions !== next.mentions;
    if (!changed) return;

    const hasSignal = next.messages > 0 || next.mentions > 0;
    const hadSignal = previous ? previous.messages > 0 || previous.mentions > 0 : false;

    if (hasSignal || hadSignal) {
      // Logs per-channel unread hook output deltas (messages, mentions) that feed sidebar rows.
      logDebug(SIDEBAR_UNREAD_DEBUG_TAG, 'useSingleChannelUnread change', {
        channelId: channelId || null,
        previous: previous || null,
        next,
      });
    }

    previousRef.current = next;
  }, [channelId, messages, mentions]);

  // primitives out, not objects - makes memo comparisons easier
  return { messages, mentions };
}
